// Reject one pallet at inspection — one mutation per card, like Approve
// (useApprovePallet.ts), so the tick lands on the card that was rejected
// instead of a full-screen "Pallet rejected" card (UF-02).
//
// The write is today's (Inspection.tsx rejectMutation): version-checked, the
// official PPECB code and reason from the Record Rejection sheet, and the
// inspector's notes as the pallet's rejection reason (the PPECB reason when
// there are none). A refusal is the card's own SubmitOverlay, as today's was
// the page's — in red on the card (QueueCard).
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { writeVerdict, type ApprovablePallet } from './useApprovePallet'

export interface Rejection { code: string; reason: string; notes: string }

export function rejectPallet(pallet: ApprovablePallet, r: Rejection): Promise<void> {
  return writeVerdict(pallet,
    inspectorId => ({
      status:                 'INSPECTED_REJECTED',
      inspected_by:           inspectorId,
      inspection_result:      'REJECTED',
      // A house reason (no PPECB code) stays out of the ppecb_* columns: a code
      // there fires the "PPECB rejected pallet" alert and non-conformance.
      ppecb_rejection_code:   r.code || null,
      ppecb_rejection_reason: r.code ? r.reason : null,
      rejection_reason:       r.code ? (r.notes || r.reason) : [r.reason, r.notes].filter(Boolean).join(' — '),
    }))
}

/**
 * One card's Reject. `onRejected` runs even if the card has unmounted by then
 * — the page passes its useHeldVerdicts `hold`, which keeps the card on screen
 * naming the pallet and its run.
 */
export function useRejectPallet<T extends ApprovablePallet>(onRejected?: (pallet: T) => void) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ pallet, rejection }: { pallet: T; rejection: Rejection }) => rejectPallet(pallet, rejection),
    onSuccess: (_d, { pallet }) => {
      onRejected?.(pallet)
      void qc.invalidateQueries({ queryKey: ['inspection-dashboard'] })
      void qc.invalidateQueries({ queryKey: ['inspection-consignment-pallets'] })
    },
  })
}
